import React, { Component, forwardRef } from 'react';
import qs from 'qs';
import PropTypes from 'prop-types';
import {
  Button,
  Grid,
  Form,
  Popup,
  Icon,
} from 'semantic-ui-react';
import Validate from './Validate';
import Helper from './Helper';
import { getIdentityTypeOpts } from '../../utils/identityTypes';
import { fetchM } from '../../actions/fetchM';
import { GENERIC_TIN_VALUES } from '../../constants/tin';

class TinFieldInput extends Component {
  static propTypes = {
    identityType: PropTypes.number,
    identityValue: PropTypes.string,
    onChange: PropTypes.func,
    value: PropTypes.string,
    isLoading: PropTypes.bool,
    tinError: PropTypes.bool,
    errMessage: PropTypes.string,
    hideMessage: PropTypes.bool,
    disabled: PropTypes.bool,
    readOnly: PropTypes.bool,
    required: PropTypes.bool,
    hideLabel: PropTypes.bool,
    validation: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({
          validate: PropTypes.func,
          option: PropTypes.object,
        }),
      ]).isRequired,
    ),
    validateRef: PropTypes.oneOfType([
      PropTypes.func,
      PropTypes.shape({}),
    ]),
    style: PropTypes.oneOfType([
      PropTypes.object,
    ]),
    bookingID: PropTypes.string,
    unitID: PropTypes.string,
    isIndonesia: PropTypes.bool,
  }
  static contextTypes = {
    store: PropTypes.object,
  }
  static defaultProps = {
    isLoading: false,
    tinError: null,
    disabled: false,
    isIndonesia: false,
  }
  constructor(props) {
    super(props);
    this.state = {
      localErrMessage: props.errMessage || null,
      hasValueChanged: false,
      searchLoading: false,
      searchTinFound: false,
      searchTimestamp: null,
      searchedTinValue: null,
      origin: window.location.origin,
    };
  }
  componentDidUpdate(prevProps, prevState) {
    const { value, identityValue } = this.props;
    const { searchTinFound, searchedTinValue, searchLoading } = this.state;

    if (identityValue && prevProps.identityValue !== identityValue && !GENERIC_TIN_VALUES.includes(value)) {
      this.handleOnChange({ target: { value: '', tinFound: false } });
      this.identityValueHasChanged();
    }

    if (value && prevProps.value !== value && !searchTinFound) {
      this.handleOnChange({ target: { value, tinFound: false } });
    } else if (prevState.searchLoading !== searchLoading && !searchLoading && searchTinFound) {
      this.handleOnChange({ target: { value: searchedTinValue, tinFound: searchTinFound } });
    }

    if (value && (prevProps.isLoading && !this.props.isLoading)) {
      this.resetHasValueChanged();
    }
  }
  componentWillUnmount() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }
  getIconName() {
    const { isLoading, tinError } = this.props;
    const { searchLoading, searchTinFound } = this.state;
    if (searchLoading || isLoading) {
      return 'circle notch';
    }
    if (tinError) {
      return 'remove circle';
    }
    if (searchTinFound || tinError === false) {
      return 'check circle';
    }
    return '';
  }
  getIconColor() {
    const { isLoading, tinError } = this.props;
    const { searchLoading, searchTinFound } = this.state;
    if (searchLoading || isLoading) {
      return 'grey';
    }
    if (tinError) {
      return 'red';
    }
    if (searchTinFound || tinError === false) {
      return 'green';
    }
    return null;
  }
  // Generate CSS classes for input styling consistency
  getInputClassName() {
    const { value, isLoading, tinError } = this.props;
    const { searchLoading, searchTinFound } = this.state;
    let className = '';
    if (searchLoading || (value && isLoading)) {
      className += 'loading circle notched icon ';
    }
    if (value && (!searchLoading || !isLoading) && !searchTinFound && tinError) {
      className += 'error';
    }
    return className.trim();
  }

  handleOnChange = (e, data) => {
    this.setState({
      hasValueChanged: true,
      localErrMessage: null,
    });

    if (this.props.onChange) {
      this.props.onChange(e, data);
    }
  }
  resetHasValueChanged = () => {
    this.setState({ hasValueChanged: false });
  }
  identityValueHasChanged = () => {
    this.setState({ searchedTinValue: null, searchTinFound: false });
  }
  handleSearchTin = async () => {
    const BUFFER_MILLISEC = 5000;
    const { identityType, identityValue } = this.props;

    this.handleOnChange({ target: { value: '', tinFound: false } });

    if (!identityType || !identityValue) {
      return;
    }

    if (this.state.searchTimestamp) {
      const now = Date.now();
      const diffTime = now - this.state.searchTimestamp;

      if (diffTime <= BUFFER_MILLISEC) {
        const roundedSeconds = Math.ceil((BUFFER_MILLISEC - diffTime) / 1000);
        this.setState({
          hasValueChanged: true,
          localErrMessage: `Cooldown ${roundedSeconds} second${roundedSeconds > 1 ? 's' : ''}`,
        });
        return;
      }
      clearInterval(this.searchTimestamp);
      this.setState({
        hasValueChanged: false,
        localErrMessage: null,
      });
    }
    this.setState({ searchedTinValue: null, searchLoading: true, searchTimestamp: Date.now() });

    // simulate a processing time during search tin
    this.searchTimeout = setTimeout(async () => {
      const idType = getIdentityTypeOpts().find(i => i.value === identityType).tin_code;
      const query = Object.assign({
        idType,
        idValue: identityValue,
        unitID: this.props.unitID || null,
        bookingID: this.props.bookingID || null,
      });
      const { store } = this.context;
      try {
        const res = await store.dispatch(fetchM(`/tin/search/?${qs.stringify(query)}`, {
          credentials: 'include',
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Origin': this.state.origin,
          },
        }));

        if (res && res.ok) {
          const data = await res.json();
          this.setState({ searchedTinValue: data.tin, searchLoading: false, searchTinFound: true, hasValueChanged: false, localErrMessage: null });
          this.handleOnChange({ target: { value: data.tin, tinFound: true } });
          return;
        }
        const errMessage = await res.text();
        throw Error(errMessage);
      } catch (err) {
        const errMessage = err.res ? await err.res.text() : err.message;
        this.setState({ searchedTinValue: null, searchLoading: false, searchTinFound: false, hasValueChanged: true, localErrMessage: errMessage });
      }
    }, 1500);
  }
  renderErrorMessage() {
    const { hideMessage, errMessage } = this.props;
    const { hasValueChanged, localErrMessage } = this.state;

    const displayMessage = hasValueChanged ? localErrMessage : errMessage;

    if (displayMessage && !hideMessage) {
      return (
        <div className="validate field" style={this.props.style}>
          <Helper type="error">{displayMessage}</Helper>
        </div>
      );
    }
    return null;
  }

  // Unified label rendering for consistent styling
  renderLabel(labelText, showInfoIcon = false) {
    const { hideLabel, required } = this.props;
    if (hideLabel) return null;

    const labelContent = `${labelText}${required ? ' *' : ''}`;

    if (!showInfoIcon) {
      // Simple label for Indonesia
      return (
        <Form.Group style={{ display: 'flex', alignItems: 'center', marginBottom: '0px' }}>
          <Grid.Column columns={1}>
            <Form.Field
              className="form-label"
              label={labelContent}
              style={{ textAlign: 'left', flex: '1', paddingLeft: '0.5em' }}
            />
          </Grid.Column>
        </Form.Group>
      );
    }

    // Label with info icon for Malaysia
    return (
      <Form.Group style={{ display: 'flex', alignItems: 'center', marginBottom: '0px' }}>
        <Grid.Column columns={1}>
          <Form.Field
            className="form-label"
            label={labelContent}
            style={{ textAlign: 'left', flex: '1', paddingLeft: '0.5em' }}
          />
        </Grid.Column>
        <Grid.Column columns={1}>
          <Popup
            className="srb"
            content={
              <div>
                <p><strong>Why It&apos;s Important:</strong> Adding a Tax Identification Number (TIN) ensures compliance with Malaysian regulations and enables the issuance of e-invoices.</p>
                <p><strong>For Individuals:</strong> All local individuals above 18 y/o have a TIN. It starts with &quot;IG&quot; followed by 9 to 11 digits.</p>
                <p><strong>For Companies:</strong> Filling in the TIN during booking creation is compulsory. <br /> Example formats: C20880050010, D4800990020.</p>
                <p><strong>For Foreigners:</strong> Providing a TIN is optional. If you are unable to obtain one, you may leave it blank.</p>
                <p><strong>Auto-Validate:</strong> The results are based on a validation check with LHDN. Ensure the details are accurate to avoid validation errors.</p>
                <a href="https://support.mhub.my/hc/en-us/articles/35235273120537-Tax-Identification-Number-TIN-Field" target="_blank" rel="noopener noreferrer">[Having trouble obtaining a correct TIN? Click here to learn more.]</a>
              </div>
            }
            on="click"
            wide
            positionFixed
            style={{ background: 'white', color: 'black', border: '3.5px solid #22242626', padding: '1.5em 2em' }}
            position="bottom center"
            trigger={
              <Button
                icon="question circle outline"
                size="small"
                style={{ padding: '0.2em 0.3em', background: 'none', marginBottom: '7px', color: 'rgb(0, 151, 161)' }}
              />
            }
            pinned
            inverted
            onOpen={(e) => {
              e.preventDefault();
            }}
            onClose={(e) => {
              e.preventDefault();
            }}
          />
        </Grid.Column>
      </Form.Group>
    );
  }

  render() {
    const {
      isIndonesia,
      validation,
      validateRef,
      onChange,
      value,
      required,
      readOnly,
      disabled,
      style,
    } = this.props;

    if (isIndonesia) {
      // Indonesia (NPWP) - Now with consistent styling
      const labelText = 'NPWP';
      const placeholderText = '12.345.678.9-012.000 or 1234567890123456';

      return (
        <Validate
          validation={validation}
          label="NPWP"
          name="taxIdentificationNo"
          ref={validateRef}
          onChange={onChange}
        >
          <Form.Input
            className={this.getInputClassName()}
            fluid
            readOnly={readOnly}
            disabled={disabled}
            autoComplete="off"
            name="taxIdentificationNo"
            placeholder={placeholderText}
            onChange={onChange}
            value={value}
            style={style}
            label={this.renderLabel(labelText, false)}
          />
        </Validate>
      );
    }
    // Malaysia - Existing functionality with consistent structure
    const labelText = 'Tax Identification Number';
    const placeholderText = `e.g. IG00000012345${required ? ' *' : ''}`;

    return (
      <Validate
        validation={validation}
        label="Tax Identification Number"
        name="taxIdentificationNo"
        ref={validateRef}
        onChange={this.handleOnChange}
        hideMessage={this.props.tinError === false}
      >
        <Form.Input
          className={this.getInputClassName()}
          fluid
          readOnly={readOnly}
          disabled={disabled}
          autoComplete="off"
          name="taxIdentificationNo"
          placeholder={placeholderText}
          onChange={this.handleOnChange}
          value={value}
          style={style}
          actionPosition="left"
          label={this.renderLabel(labelText, true)}
          icon={
            this.state.searchLoading || value ? (
              <Icon
                loading={this.state.searchLoading || this.props.isLoading}
                name={this.getIconName()}
                color={this.getIconColor()}
              />
            ) : null
          }
          action={
            <Popup
              content="Search TIN from LHDN"
              mouseEnterDelay={500}
              size="tiny"
              trigger={
                <Button
                  style={{ backgroundColor: '#f9fafb', border: '1px solid #ccc' }}
                  icon="search"
                  type="button"
                  disabled={readOnly || this.state.searchTinFound || this.state.searchLoading}
                  onClick={this.handleSearchTin}
                />
              }
            />
          }
        />
        {this.renderErrorMessage()}
      </Validate>
    );
  }
}

export default forwardRef((props, ref) => <TinFieldInput {...props} validateRef={ref} />);



// WEBPACK FOOTER //
// ./src/components/AppForm/TinFieldInput.jsx